import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type EventListItem, type MyStatus } from '../lib/api';
import { DISCIPLINES, MON, TYPES, dShort, plural, timeLabel, undoStatus, wd, ymd } from '../lib/domain';
import { eventTooltip, firstLine, stripMd } from '../lib/md';
import { Avatar, DiscChips, DiscIcon, ErrorBox, Icon, Loading, PageHead } from '../ui';
import { useToast } from '../hooks';
import { dc } from '../lib/style';

const PAGE = 200;
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss');

export function RsvpButton({ e }: { e: EventListItem }) {
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: (v: { status: 'yes' | 'maybe' | 'none'; undo?: boolean }) =>
      api(`/events/${e.id}/rsvp`, { method: 'PUT', body: { status: v.status } }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['events'] });
      void qc.invalidateQueries({ queryKey: ['event', e.id] });
      if (v.undo) return toast('Rückgängig gemacht');
      // "Rückgängig" restores the status from before the tap (the server keeps the old list position)
      const before = undoStatus(e.my_status);
      toast(v.status === 'yes' ? `Zugesagt: ${e.title}` : `Abgesagt: ${e.title}`, () =>
        m.mutate({ status: before, undo: true }),
      );
    },
    onError: err => toast(err.message),
  });
  if (e.cancelled) return <span className="st st-cancel">Abgesagt</span>;
  const s: MyStatus = e.my_status;
  const label = { none: 'Zusagen', yes: 'Dabei', wait: 'Warteliste', maybe: 'Vielleicht' }[s];
  return (
    <button
      className="rsvp-btn"
      data-s={s}
      disabled={m.isPending}
      aria-label={`${label}: ${e.title}`}
      onClick={ev => {
        ev.stopPropagation();
        m.mutate({ status: s === 'none' ? 'yes' : 'none' });
      }}
    >
      {s === 'yes' && <Icon name="check" sw={2.4} />}
      {label}
    </button>
  );
}

export function Card({ e }: { e: EventListItem }) {
  const nav = useNavigate();
  const past = e.past;
  const confirmed = e.capacity ? Math.min(e.yes, e.capacity) : e.yes;
  const wait = e.capacity ? Math.max(0, e.yes - e.capacity) : 0;
  const snip = firstLine(e.description).slice(0, 160);
  // phones have no hover: the full text and who is coming fold open in the tile
  const [open, setOpen] = useState(false);
  const full = stripMd(e.description)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  const foldable = full.length > snip.length + 5 || e.preview.length > 0;
  return (
    <div
      className={`card${past ? ' is-past' : ''}${e.cancelled ? ' is-cancel' : ''}`}
      title={eventTooltip(e, `${dShort(new Date(e.starts_at))} · ${timeLabel(e)}`)}
      role="button"
      tabIndex={0}
      style={dc(e.discipline)}
      onClick={() => void nav(`/termine/${e.id}`)}
      onKeyDown={ev => {
        // Enter on the RSVP button inside must not also open the event
        if (ev.key === 'Enter' && ev.target === ev.currentTarget) void nav(`/termine/${e.id}`);
      }}
    >
      <DiscIcon disc={e.discipline} title={DISCIPLINES[e.discipline]} />
      <div className="c-body">
        <div className="c-meta">
          <span className="c-time">{timeLabel(e)}</span>
          <span className="tag">
            {DISCIPLINES[e.discipline]} · {TYPES[e.type]}
          </span>
          {e.series_id && (
            <span className="badge">
              <Icon name="repeat" />
              Serie
            </span>
          )}
        </div>
        <div className="c-title">{e.title}</div>
        {open ? (
          <div className="c-full" id={`c-full-${e.id}`}>
            {full && <p>{full.length > 1200 ? `${full.slice(0, 1200).trimEnd()} …` : full}</p>}
            {e.preview.length > 0 && (
              <p className="c-names">
                <b>Dabei:</b> {e.preview.map(p => p.name).join(', ')}
                {confirmed > e.preview.length ? ` und ${confirmed - e.preview.length} weitere` : ''}
              </p>
            )}
          </div>
        ) : (
          snip && <div className="c-snip">{snip}</div>
        )}
        <div className="c-foot">
          {e.location && (
            <span className="ic">
              <Icon name="pin" />
              <span>{e.location}</span>
            </span>
          )}
          <span className="ic">
            <span className="avs">
              {e.preview.map((p, i) => (
                <Avatar key={i} person={p} />
              ))}
            </span>
            <span>
              {confirmed} dabei{e.maybe ? ` · ${e.maybe} vielleicht` : ''}
              {wait ? ` · ${wait} Warteliste` : ''}
            </span>
          </span>
          {e.capacity && (
            <span className="ic">
              <span className={`meter${confirmed >= e.capacity ? ' full' : ''}`}>
                <i style={{ width: `${Math.min(100, (confirmed / e.capacity) * 100)}%` }} />
              </span>{' '}
              {confirmed}/{e.capacity}
            </span>
          )}
          {e.comments > 0 && (
            <span className="ic">
              <Icon name="chat" />
              {e.comments}
            </span>
          )}
          {foldable && (
            <button
              className="c-fold"
              aria-expanded={open}
              aria-controls={`c-full-${e.id}`}
              aria-label={open ? `Weniger anzeigen: ${e.title}` : `Mehr anzeigen: ${e.title}`}
              onClick={ev => {
                ev.stopPropagation();
                setOpen(!open);
              }}
              onKeyDown={ev => ev.stopPropagation()}
            >
              <span aria-hidden="true">{open ? '▴' : '▾'}</span>
              {open ? 'Weniger' : 'Mehr'}
            </button>
          )}
        </div>
      </div>
      {!past && <RsvpButton e={e} />}
    </div>
  );
}

export default function Termine() {
  const [scope, setScope] = useState<'upcoming' | 'archive'>('upcoming');
  const [q, setQ] = useState('');
  const [discs, setDiscs] = useState<Set<string>>(new Set());
  const [mine, setMine] = useState(false);
  // Pages of PAGE events; the archive (years of history) loads older pages on demand
  const {
    data: pages,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['events', scope],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<EventListItem[]>(`/events?scope=${scope}&limit=${PAGE}&offset=${pageParam}`),
    getNextPageParam: (last, all) => (last.length < PAGE ? undefined : all.length * PAGE),
  });
  const data = useMemo(() => pages?.pages.flat(), [pages]);

  const list = useMemo(() => {
    const toks = norm(q).split(/\s+/).filter(Boolean);
    return (data || []).filter(
      e =>
        (!discs.size || discs.has(e.discipline)) &&
        (!mine || e.my_status !== 'none') &&
        (!toks.length ||
          toks.every(t =>
            norm(
              `${e.title} ${e.description} ${e.location} ${DISCIPLINES[e.discipline]} ${TYPES[e.type]} ${e.organizer?.name || ''}`,
            ).includes(t),
          )),
    );
  }, [data, q, discs, mine]);

  const groups = useMemo(() => {
    const out: { key: string; label: string; days: { key: string; date: Date; events: EventListItem[] }[] }[] = [];
    for (const e of list) {
      const d = new Date(e.starts_at),
        mk = `${d.getFullYear()}-${d.getMonth()}`,
        dk = ymd(d);
      let g = out[out.length - 1];
      if (!g || g.key !== mk) {
        g = { key: mk, label: `${MON[d.getMonth()]} ${d.getFullYear()}`, days: [] };
        out.push(g);
      }
      let day = g.days[g.days.length - 1];
      if (!day || day.key !== dk) {
        day = { key: dk, date: d, events: [] };
        g.days.push(day);
      }
      day.events.push(e);
    }
    return out;
  }, [list]);
  const today = ymd(new Date());

  return (
    <>
      <PageHead
        title="Termine"
        right={
          <Link className="btn btn-primary" to="/termine/neu">
            <Icon name="plus" sw={2.2} />
            Termin anlegen
          </Link>
        }
      />
      <div className="toolbar" role="search">
        <label className="field">
          <Icon name="search" sw={1.8} />
          <input
            type="search"
            placeholder="Suchen: Titel, Ort, Beschreibung …"
            value={q}
            onChange={e => setQ(e.target.value)}
            aria-label="Termine durchsuchen"
          />
        </label>
        <div className="seg" role="group" aria-label="Zeitraum">
          <button aria-pressed={scope === 'upcoming'} onClick={() => setScope('upcoming')}>
            Kommend
          </button>
          <button aria-pressed={scope === 'archive'} onClick={() => setScope('archive')}>
            Archiv
          </button>
        </div>
        <button className="toggle" aria-pressed={mine} onClick={() => setMine(!mine)}>
          <Icon name="user" sw={1.8} />
          Nur meine
        </button>
        <DiscChips value={discs} onChange={setDiscs} />
      </div>
      {isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorBox error={error} />
      ) : (
        <>
          <div className="resbar">
            <span>
              {plural(list.length, 'Termin', 'Termine')}
              {scope === 'archive' ? ' im Archiv' : ''}
            </span>
          </div>
          {!list.length && (
            <div className="empty">
              <b>Keine Termine gefunden.</b>
              <p>Probiere einen anderen Suchbegriff oder entferne Filter.</p>
            </div>
          )}
          {groups.map(g => (
            <section key={g.key}>
              <h2 className="month-h">
                {g.label}{' '}
                <small>
                  {plural(
                    g.days.reduce((n, d) => n + d.events.length, 0),
                    'Termin',
                    'Termine',
                  )}
                </small>
              </h2>
              {g.days.map(d => (
                <div key={d.key} className={`day${d.key === today ? ' is-today' : ''}`}>
                  <div className="day-date">
                    <b>{d.date.getDate()}</b>
                    <span>{wd(d.date)}</span>
                    {d.key === today && <em>Heute</em>}
                  </div>
                  <div className="day-events">
                    {d.events.map(e => (
                      <Card key={e.id} e={e} />
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ))}
          {hasNextPage && (
            <div className="more">
              <button className="btn" disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
                {isFetchingNextPage ? 'Lädt …' : scope === 'archive' ? 'Ältere Termine laden' : 'Weitere Termine laden'}
              </button>
              {(q || discs.size > 0 || mine) && <p>Suche und Filter beziehen sich auf die bisher geladenen Termine.</p>}
            </div>
          )}
        </>
      )}
    </>
  );
}
